(() => {
  if (customElements.get('abok-header')) return;

  class AbokHeader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.options = { signal: this.abortController.signal };
      this.desktopQuery = window.matchMedia('(min-width: 1200px)');
      this.triggers = [...this.querySelectorAll('[data-abok-menu-trigger]')];
      this.panels = [...this.querySelectorAll('[data-abok-menu-panel]')];
      this.screen = this.querySelector('[data-abok-menu-screen]');
      this.drawer = this.querySelector('[data-abok-drawer]');
      this.drawerPanel = this.querySelector('[data-abok-drawer-panel]');
      this.drawerSheetHeader = this.querySelector('[data-abok-drawer-sheet-header]');
      this.countryModal = this.querySelector('[data-abok-site-sheet]');
      this.siteSheetDialog = this.querySelector('[data-abok-site-sheet-dialog]');
      this.siteSheetHeader = this.querySelector('[data-abok-site-sheet-header]');
      this.sectionWrapper = this.closest('.shopify-section');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.sheetResetTimers = new Map();
      this.activeTrigger = null;
      this.closeTimer = null;
      this.lastScrollY = window.scrollY;

      this.bindDesktopMenu();
      this.bindDrawer();
      this.bindStickyHeader();
      this.updateHeight();

      this.addEventListener('abok:purchase-result', (event) => {
        if (event.detail?.status === 'success' || event.detail?.status === 'partial') {
          this.closeDrawer(false);
        }
      }, this.options);
      window.addEventListener('resize', () => this.onViewportResize(), this.options);
      window.visualViewport?.addEventListener('resize', () => this.onViewportResize(), this.options);
      this.desktopQuery.addEventListener('change', (event) => {
        if (event.matches) this.closeDrawer(false);
      }, this.options);
      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), this.options);
      document.addEventListener('pointerdown', (event) => this.onDocumentPointerdown(event), this.options);
    }

    disconnectedCallback() {
      const hadOpenOverlay = Boolean(this.drawer?.classList.contains('is-open') || this.countryModal?.open);

      this.abortController?.abort();
      window.clearTimeout(this.closeTimer);
      this.sheetResetTimers?.forEach((timer) => window.clearTimeout(timer));
      this.sheetResetTimers?.clear();
      this.countryModal?.hide();
      this.drawer?.classList.remove('is-open', 'has-site-sheet');
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.drawer?.setAttribute('inert', '');
      this.drawerPanel?.setAttribute('inert', '');
      this.querySelectorAll('[data-abok-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('abok-drawer-open');
      if (hadOpenOverlay && typeof removeTrapFocus === 'function') removeTrapFocus();
      this.initialized = false;
    }

    bindDesktopMenu() {
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (event) => this.onTriggerClick(event, trigger), this.options);
        trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, index), this.options);
        trigger.addEventListener('mouseenter', () => {
          if (this.shouldOpenOnHover()) this.openPanel(trigger);
        }, this.options);
        trigger.addEventListener('mouseleave', () => this.schedulePanelClose(), this.options);
      });

      this.panels.forEach((panel) => {
        panel.addEventListener('mouseenter', () => window.clearTimeout(this.closeTimer), this.options);
        panel.addEventListener('mouseleave', () => this.schedulePanelClose(), this.options);
      });

      this.screen?.addEventListener('click', () => this.closePanel(), this.options);
    }

    bindDrawer() {
      this.querySelectorAll('[data-abok-drawer-open]').forEach((button) => {
        button.addEventListener('click', () => this.openDrawer(button), this.options);
      });

      this.querySelectorAll('[data-abok-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer(), this.options);
      });

      this.bindSheetGesture({
        handle: this.drawerSheetHeader,
        sheet: this.drawerPanel,
        close: () => this.closeDrawer(),
        isEnabled: () => this.drawer?.classList.contains('is-open') && !this.desktopQuery.matches,
      });

      this.bindSheetGesture({
        handle: this.siteSheetHeader,
        sheet: this.siteSheetDialog,
        close: () => this.countryModal?.hide(),
        isEnabled: () => Boolean(this.countryModal?.open && !this.desktopQuery.matches),
      });

      this.countryModal?.addEventListener('modal:beforeShow', () => this.onSiteSheetBeforeShow(), this.options);
      this.countryModal?.addEventListener('modal:afterHide', () => this.onSiteSheetAfterHide(), this.options);
    }

    bindStickyHeader() {
      if (!this.sectionWrapper || this.dataset.stickyMode === 'none') return;

      this.sectionWrapper.classList.add('is-sticky');
      if (this.dataset.stickyMode !== 'on-scroll-up') return;

      window.addEventListener('scroll', () => {
        const currentY = window.scrollY;
        const movingDown = currentY > this.lastScrollY;
        const pastHeader = currentY > this.offsetHeight * 2;

        this.sectionWrapper.classList.toggle('is-hidden', movingDown && pastHeader && !this.hasOpenUi());
        this.lastScrollY = currentY;
      }, { ...this.options, passive: true });
    }

    shouldOpenOnHover() {
      return this.desktopQuery.matches && this.dataset.hoverMenu === 'true';
    }

    getPanel(trigger) {
      const panelId = trigger?.getAttribute('aria-controls');
      if (!panelId) return null;
      return this.querySelector(`#${CSS.escape(panelId)}`);
    }

    togglePanel(trigger) {
      if (this.activeTrigger === trigger) {
        this.closePanel();
      } else {
        this.openPanel(trigger);
      }
    }

    onTriggerClick(event, trigger) {
      if (this.shouldOpenOnHover() && event.detail > 0) {
        this.openPanel(trigger);
        return;
      }

      this.togglePanel(trigger);
    }

    openPanel(trigger) {
      if (!this.desktopQuery.matches) return;

      const panel = this.getPanel(trigger);
      if (!panel) return;

      window.clearTimeout(this.closeTimer);
      this.triggers.forEach((item) => {
        const active = item === trigger;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-expanded', String(active));
      });
      this.panels.forEach((item) => {
        const active = item === panel;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
      });

      this.activeTrigger = trigger;
      this.classList.add('is-menu-open');
    }

    schedulePanelClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closePanel(), 140);
    }

    closePanel({ restoreFocus = false } = {}) {
      window.clearTimeout(this.closeTimer);
      const previousTrigger = this.activeTrigger;

      this.triggers.forEach((trigger) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.panels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      });

      this.activeTrigger = null;
      this.classList.remove('is-menu-open');
      if (restoreFocus) previousTrigger?.focus();
    }

    onTriggerKeydown(event, index) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const target = this.triggers[(index + direction + this.triggers.length) % this.triggers.length];
        target?.focus();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openPanel(event.currentTarget);
        if (event.key === 'ArrowDown') {
          this.getPanel(event.currentTarget)?.querySelector('a, button, [tabindex]:not([tabindex="-1"])')?.focus();
        }
      }
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;

      if (this.countryModal?.open || this.countryModal?.hasAttribute('active')) return;

      if (this.drawer?.classList.contains('is-open')) {
        event.preventDefault();
        this.closeDrawer();
      } else if (this.activeTrigger) {
        event.preventDefault();
        this.closePanel({ restoreFocus: true });
      }
    }

    onDocumentPointerdown(event) {
      if (this.activeTrigger && !this.contains(event.target)) this.closePanel();
    }

    openDrawer(opener) {
      if (!this.drawer) return;

      this.closePanel();
      this.clearSheetReset(this.drawerPanel);
      this.resetSheetPosition(this.drawerPanel, true);
      this.drawerOpener = opener;
      this.updateDrawerTop();
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');
      this.drawerPanel?.removeAttribute('inert');
      opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('abok-drawer-open');

      requestAnimationFrame(() => {
        const firstFocus = this.drawerPanel?.querySelector('[data-abok-drawer-close]');
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawerPanel, firstFocus);
        } else {
          firstFocus?.focus();
        }
      });
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer?.classList.contains('is-open')) return;

      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.drawerPanel?.setAttribute('inert', '');
      document.body.classList.remove('abok-drawer-open');
      this.querySelectorAll('[data-abok-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.scheduleSheetReset(this.drawerPanel);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(restoreFocus ? this.drawerOpener : null);
      } else if (restoreFocus) {
        this.drawerOpener?.focus();
      }
    }

    hasOpenUi() {
      return Boolean(this.activeTrigger || this.drawer?.classList.contains('is-open') || this.countryModal?.open);
    }

    updateHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.getBoundingClientRect().height.toFixed(1)}px`);
    }

    onViewportResize() {
      this.updateHeight();
      this.updateDrawerTop();
    }

    updateDrawerTop() {
      if (!this.drawer || (this.initialized && !this.drawer.classList.contains('is-open') && !this.drawerOpener)) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const headerBottom = this.getBoundingClientRect().bottom;
      const top = Math.max(0, Math.min(headerBottom, viewportHeight - 240));
      this.drawer.style.setProperty('--abok-drawer-top', `${Math.round(top)}px`);
    }

    onSiteSheetBeforeShow() {
      this.clearSheetReset(this.siteSheetDialog);
      this.resetSheetPosition(this.siteSheetDialog, true);

      if (!this.drawer?.classList.contains('is-open')) return;

      this.drawer.classList.add('has-site-sheet');
      this.drawerPanel?.setAttribute('inert', '');
    }

    onSiteSheetAfterHide() {
      this.scheduleSheetReset(this.siteSheetDialog);
      if (!this.drawer?.classList.contains('is-open')) return;

      this.drawer.classList.remove('has-site-sheet');
      this.drawerPanel?.removeAttribute('inert');
      const returnTarget = this.countryModal?.openedBy;

      requestAnimationFrame(() => {
        if (!this.drawer?.classList.contains('is-open')) return;
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawerPanel, returnTarget);
        } else {
          returnTarget?.focus();
        }
      });
    }

    bindSheetGesture({ handle, sheet, close, isEnabled }) {
      if (!handle || !sheet) return;

      let gesture = null;
      let suppressClick = false;

      handle.addEventListener('click', (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      }, { ...this.options, capture: true });

      handle.addEventListener('pointerdown', (event) => {
        if (!isEnabled() || event.isPrimary === false) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('[data-abok-sheet-no-drag]')) return;

        this.clearSheetReset(sheet);
        gesture = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastDelta: 0,
          lastTime: performance.now(),
          velocity: 0,
          delta: 0,
          moved: false,
          rejected: false,
          vertical: false,
        };
        handle.setPointerCapture?.(event.pointerId);
      }, this.options);

      handle.addEventListener('pointermove', (event) => {
        if (!gesture || event.pointerId !== gesture.pointerId) return;

        const deltaX = event.clientX - gesture.startX;
        const deltaY = event.clientY - gesture.startY;

        if (!gesture.moved && Math.hypot(deltaX, deltaY) >= 6) {
          gesture.moved = true;
          gesture.rejected = Math.abs(deltaX) >= Math.abs(deltaY) * 0.85;
          gesture.vertical = !gesture.rejected;
        }

        if (!gesture.vertical || gesture.rejected) return;

        const nextDelta = Math.max(0, deltaY);
        const now = performance.now();
        const elapsed = Math.max(1, now - gesture.lastTime);
        gesture.velocity = (nextDelta - gesture.lastDelta) / elapsed;
        gesture.delta = nextDelta;
        gesture.lastDelta = nextDelta;
        gesture.lastTime = now;

        sheet.classList.add('is-dragging');
        sheet.style.setProperty('--abok-sheet-drag-y', `${nextDelta.toFixed(1)}px`);
        event.preventDefault();
      }, { ...this.options, passive: false });

      const finishGesture = (event, cancelled = false) => {
        if (!gesture || event.pointerId !== gesture.pointerId) return;

        if (handle.hasPointerCapture?.(event.pointerId)) handle.releasePointerCapture(event.pointerId);
        if (gesture.moved) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
        }

        const distanceThreshold = Math.min(140, Math.max(84, sheet.offsetHeight * 0.16));
        const fastDownwardFlick = gesture.delta >= 32 && gesture.velocity >= 0.65;
        const shouldClose = !cancelled
          && gesture.vertical
          && (gesture.delta >= distanceThreshold || fastDownwardFlick);

        sheet.classList.remove('is-dragging');
        gesture = null;

        if (shouldClose) {
          close();
          this.scheduleSheetReset(sheet);
        } else {
          sheet.style.setProperty('--abok-sheet-drag-y', '0px');
          this.scheduleSheetReset(sheet);
        }
      };

      handle.addEventListener('pointerup', (event) => finishGesture(event), this.options);
      handle.addEventListener('pointercancel', (event) => finishGesture(event, true), this.options);
    }

    resetSheetPosition(sheet, immediate = false) {
      if (!sheet) return;

      sheet.classList.remove('is-dragging');
      if (immediate || this.reducedMotion.matches) {
        sheet.style.removeProperty('--abok-sheet-drag-y');
      } else {
        sheet.style.setProperty('--abok-sheet-drag-y', '0px');
      }
    }

    clearSheetReset(sheet) {
      const timer = this.sheetResetTimers.get(sheet);
      if (timer) window.clearTimeout(timer);
      this.sheetResetTimers.delete(sheet);
    }

    scheduleSheetReset(sheet) {
      if (!sheet) return;

      this.clearSheetReset(sheet);
      const delay = this.reducedMotion.matches ? 0 : 300;
      const timer = window.setTimeout(() => {
        sheet.style.removeProperty('--abok-sheet-drag-y');
        sheet.classList.remove('is-dragging');
        this.sheetResetTimers.delete(sheet);
      }, delay);
      this.sheetResetTimers.set(sheet, timer);
    }
  }

  customElements.define('abok-header', AbokHeader);
})();
